import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CheckCircle as CheckCircleIcon,
    Assignment as AssignmentIcon,
    Event as EventIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import AdminLayout from './AdminLayout';
import { formService, submissionService } from '../services/firestoreService';
import { useAuth } from '../hooks/useAuth';
import competitionService from '../services/competitionService';

export default function StudentDashboard() {
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();

    const [publishedForms, setPublishedForms] = useState([]);
    const [competitions, setCompetitions] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchData();
    }, [user]);
    const isUpcoming = (date) => {
        if (!date) return false;
        return new Date(date) >= new Date();
    };
    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');

            // ✅ Fetch ALL forms then filter for PUBLISHED ONLY
            const allForms = await formService.getAllForms();
            const published = Array.isArray(allForms)
                ? allForms.filter(f => f.isPublished === true && f.status !== 'submitted')
                : [];
            setPublishedForms(published);

            // ✅ Fetch competitions
            const compsData = await competitionService.getCompetitions();

            const upcomingCompetitions = Array.isArray(compsData)
                ? compsData.filter(comp => isUpcoming(comp.lastDateToApply))
                : [];

            setCompetitions(upcomingCompetitions);

            // ✅ Fetch user's submissions
            if (user?.uid) {
                const subsData = await submissionService.getSubmissionsByUserId(user.uid);
                setSubmissions(Array.isArray(subsData) ? subsData : []);
            }
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const handleStartForm = (formId) => {
        navigate(`/form-submission/${formId}`);
    };

    const getCompletionStatus = (formId) => {
        return submissions.some(s => s.formId === formId);
    };

    if (loading) {
        return (
            <AdminLayout title="Dashboard">
                <div className="flex justify-center items-center min-h-screen">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </AdminLayout>
        );
    }

    const completedCount = submissions.length;
    const totalForms = publishedForms.length;
    const upcomingCompetitionsCount = competitions.length;


    return (
        <AdminLayout title="Dashboard">
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
                <div className="max-w-6xl mx-auto space-y-8">
                    {/* ============== ERROR ALERT ============== */}
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex justify-between">
                            <span>{error}</span>
                            <button onClick={() => setError('')} className="font-bold">×</button>
                        </div>
                    )}

                    {/* ============== WELCOME HEADER ============== */}
                    <div>
                        <h1 className="text-4xl font-bold text-gray-900">
                            👋 Welcome, {userProfile?.firstName || 'Student'}!
                        </h1>
                        <p className="text-gray-600 mt-2">Complete your forms and participate in competitions</p>
                    </div>

                    {/* ============== KPI CARDS ============== */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Available Forms */}
                        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-600">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm font-semibold">📋 Available Forms</p>
                                    <p className="text-4xl font-bold text-gray-900 mt-2">{totalForms}</p>
                                    <p className="text-xs text-gray-500 mt-1">Forms to complete</p>
                                </div>
                                <AssignmentIcon className="text-blue-600 text-5xl opacity-20" />
                            </div>
                        </div>

                        {/* Completed Forms */}
                        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-600">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm font-semibold">✅ Completed</p>
                                    <p className="text-4xl font-bold text-gray-900 mt-2">{completedCount}</p>
                                    <p className="text-xs text-gray-500 mt-1">Forms submitted</p>
                                </div>
                                <CheckCircleIcon className="text-green-600 text-5xl opacity-20" />
                            </div>
                        </div>

                        {/* In Progress */}
                        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm font-semibold">🏆 Upcoming Competitions</p>
                                    <p className="text-4xl font-bold text-gray-900 mt-2">
                                        {upcomingCompetitionsCount}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Open for application
                                    </p>
                                </div>
                                <EventIcon className="text-orange-600 text-5xl opacity-20" />
                            </div>
                        </div>
                    </div>

                    {/* ============== AVAILABLE FORMS SECTION ============== */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <AssignmentIcon className="text-blue-600" />
                            📋 Available Forms
                        </h2>

                        {publishedForms.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {publishedForms.map(form => {
                                    const isCompleted = getCompletionStatus(form.id);
                                    return (
                                        <div
                                            key={form.id}
                                            className={`p-6 border-2 rounded-lg transition hover:shadow-lg ${isCompleted
                                                ? 'border-green-200 bg-green-50'
                                                : 'border-blue-200 bg-blue-50'
                                                }`}
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <h3 className="text-lg font-bold text-gray-900 flex-1">{form.title}</h3>
                                                {isCompleted && (
                                                    <span className="px-3 py-1 bg-green-200 text-green-800 text-xs rounded-full font-semibold whitespace-nowrap ml-2">
                                                        ✅ Completed
                                                    </span>
                                                )}
                                            </div>

                                            <p className="text-gray-600 text-sm mb-4">
                                                {form.description || 'No description available'}
                                            </p>

                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-gray-500">
                                                    {form.questions?.length || 0} questions
                                                </span>
                                                <button
                                                    onClick={() => handleStartForm(form.id)}
                                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${isCompleted
                                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                                        }`}
                                                >
                                                    {isCompleted ? 'Review' : 'Start'} <ArrowForwardIcon className="text-lg" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <AssignmentIcon className="text-6xl opacity-20 mx-auto mb-4" />
                                <p>No forms available at this time</p>
                                <p className="text-sm mt-2">Check back soon for new forms</p>
                            </div>
                        )}
                    </div>

                    {/* ============== COMPETITIONS SECTION ============== */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <EventIcon className="text-red-600" />
                            🏆 Competitions
                        </h2>

                        {competitions.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {competitions.map(comp => (
                                    <div
                                        key={comp.id}
                                        className="p-6 border border-red-200 bg-red-50 rounded-lg hover:shadow-lg transition"
                                    >
                                        <h3 className="text-lg font-bold text-gray-900 mb-2">{comp.title}</h3>
                                        <p className="text-gray-600 text-sm mb-4">
                                            {comp.description || 'No description available'}
                                        </p>

                                        <div className="space-y-2 text-sm text-gray-600 mb-4">
                                            {comp.lastDateToApply && (
                                                <p>
                                                    📅 Apply by:{' '}
                                                    <span className="font-semibold">
                                                        {new Date(comp.lastDateToApply).toLocaleDateString()}
                                                    </span>
                                                </p>
                                            )}
                                            {comp.resultDeclarationDate && (
                                                <p>
                                                    🏁 Results:{' '}
                                                    <span className="font-semibold">
                                                        {new Date(comp.resultDeclarationDate).toLocaleDateString()}
                                                    </span>
                                                </p>
                                            )}
                                            <p>
                                                🚀 Participating Startups:{' '}
                                                <span className="font-semibold">
                                                    {comp.startupIds?.length || 0}
                                                </span>
                                            </p>
                                        </div>

                                        <button
                                            onClick={() => {
                                                if (comp.competitionLink) {
                                                    window.open(comp.competitionLink, "_blank");
                                                } else {
                                                    alert("Competition details link not available");
                                                }
                                            }}
                                            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition"
                                        >
                                            View Details <ArrowForwardIcon className="inline text-lg" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <EventIcon className="text-6xl opacity-20 mx-auto mb-4" />
                                <p>No competitions available at this time</p>
                                <p className="text-sm mt-2">Check back soon for exciting opportunities</p>
                            </div>
                        )}
                    </div>


                </div>
            </div>
        </AdminLayout>
    );
}

